import { useEffect, useRef } from "react";

interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogLocale?: string;
  twitterCard?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noIndex?: boolean;
}

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://kakobuy.one";

export function useSEO(data: SEOData) {
  const jsonLdRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    document.title = data.title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", data.description);

    if (data.keywords) {
      setMeta("keywords", data.keywords);
    }

    const canonicalPath = data.canonicalPath || "/";
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    let canonicalEl = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute("href", canonicalUrl);

    const robotsContent = data.noIndex ? "noindex, nofollow" : "index, follow";
    setMeta("robots", robotsContent);

    const ogTitle = data.ogTitle || data.title;
    const ogDesc = data.ogDescription || data.description;
    const ogType = data.ogType || "website";
    const ogLocale = data.ogLocale || "pl_PL";

    setMeta("og:title", ogTitle, true);
    setMeta("og:description", ogDesc, true);
    setMeta("og:url", canonicalUrl, true);
    setMeta("og:type", ogType, true);
    setMeta("og:locale", ogLocale, true);

    const twitterCard = data.twitterCard || "summary_large_image";
    setMeta("twitter:card", twitterCard);
    setMeta("twitter:title", ogTitle);
    setMeta("twitter:description", ogDesc);

    const existingJsonLd = jsonLdRef.current;
    if (existingJsonLd && existingJsonLd.parentNode) {
      existingJsonLd.parentNode.removeChild(existingJsonLd);
    }
    jsonLdRef.current = null;

    if (data.jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      const jsonArray = Array.isArray(data.jsonLd) ? data.jsonLd : [data.jsonLd];
      script.textContent = JSON.stringify(
        jsonArray.length === 1 ? jsonArray[0] : jsonArray,
        null,
        0
      );
      document.head.appendChild(script);
      jsonLdRef.current = script;
    }

    return () => {
      const ld = jsonLdRef.current;
      if (ld && ld.parentNode) {
        ld.parentNode.removeChild(ld);
      }
      jsonLdRef.current = null;
    };
  }, [
    data.title,
    data.description,
    data.keywords,
    data.canonicalPath,
    data.ogTitle,
    data.ogDescription,
    data.ogType,
    data.ogLocale,
    data.twitterCard,
    data.jsonLd,
    data.noIndex,
  ]);
}

export { SITE_URL };